import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Seed a list page's filters from the URL query string.
 *
 * Dashboard tiles link to the list page that backs them — "Leaves Applied: 5"
 * opens `/leaves?leave_status=pending` — so the list has to arrive already
 * filtered to the same rows the tile counted. Pages keep their filters in
 * local state, so this only supplies the *initial* value:
 *
 *   const [filters, setFilters] = useState(useUrlFilters(DEFAULTS));
 *
 * Later edits to the filters (or to the URL) are the page's business; the URL
 * is read once, on mount, and never written back.
 *
 * @param defaults  the page's own default filters
 * @param allowedKeys  which query params may be read. Required for pages whose
 *   defaults are `{}` — without it there would be no key list to validate
 *   against and any stray query param would be forwarded to the API.
 */
export function useUrlFilters<T extends Record<string, any>>(
  defaults: T,
  allowedKeys?: string[],
): T {
  const [searchParams] = useSearchParams();
  // Freeze the first render's params: this feeds useState initialisers, which
  // ignore later values anyway, and re-reading would churn the memo for nothing.
  const initial = useRef(searchParams);

  return useMemo(() => {
    const allowed = new Set(allowedKeys ?? Object.keys(defaults));
    const next: Record<string, any> = { ...defaults };
    initial.current.forEach((value, key) => {
      if (allowed.has(key) && value !== '') next[key] = value;
    });
    return next as T;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useUrlFilters;
